import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { ReactComponent as TrashIcon } from '../../assets/icons/trashIcon.svg'
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg'

import Portal from '../../components/Portal/Portal'

import styles from './styles.module.scss'
import ImageComponent from '../Image/ImageComponent'

const PhotoGalleryModal = ({ open, title, files, initialIndex = 0, onClose, onRequestDelete }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [pendingDeleteFileId, setPendingDeleteFileId] = useState(null)
  const [pendingNextIndex, setPendingNextIndex] = useState(null)

  const touchStartRef = useRef({ x: 0, y: 0, at: 0 })
  const touchActiveRef = useRef(false)

  useEffect(() => {
    if (!open) return

    const prevOverflow = document.body.style.overflow
    const prevPaddingRight = document.body.style.paddingRight

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`

    return () => {
      document.body.style.overflow = prevOverflow
      document.body.style.paddingRight = prevPaddingRight
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(files.length - 1, 0)))
    setPendingDeleteFileId(null)
    setPendingNextIndex(null)
  }, [open, initialIndex, files.length])

  const goPrev = () => {
    const total = files.length
    if (total <= 1) return
    setActiveIndex((prev) => (prev - 1 + total) % total)
  }

  const goNext = () => {
    const total = files.length
    if (total <= 1) return
    setActiveIndex((prev) => (prev + 1) % total)
  }

  const handleTouchStart = (e) => {
    const t = e.touches && e.touches[0]
    if (!t) return
    touchActiveRef.current = true
    touchStartRef.current = { x: t.clientX, y: t.clientY, at: Date.now() }
  }

  const handleTouchMove = (e) => {
    if (!touchActiveRef.current) return
    const t = e.touches && e.touches[0]
    if (!t) return

    const dx = t.clientX - touchStartRef.current.x
    const dy = t.clientY - touchStartRef.current.y

    if (Math.abs(dx) > Math.abs(dy)) e.preventDefault()
  }

  const handleTouchEnd = (e) => {
    if (!touchActiveRef.current) return
    touchActiveRef.current = false

    const t = e.changedTouches && e.changedTouches[0]
    if (!t) return

    const dx = t.clientX - touchStartRef.current.x
    const dy = t.clientY - touchStartRef.current.y
    const dt = Date.now() - touchStartRef.current.at

    const absDx = Math.abs(dx)
    const absDy = Math.abs(dy)

    const minSwipe = 50
    const maxTime = 600

    if (absDx < minSwipe) return
    if (absDx < absDy * 1.2) return
    if (dt > maxTime) return

    if (dx < 0) goNext()
    if (dx > 0) goPrev()
  }

  const handleDeleteActive = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (typeof onRequestDelete !== 'function') return

    const total = files.length
    if (!total) return

    const file = files[activeIndex]
    if (!file) return

    const nextIndex = total === 1 ? 0 : activeIndex < total - 1 ? activeIndex : activeIndex - 1
    setPendingDeleteFileId(file.id)
    setPendingNextIndex(nextIndex)

    onRequestDelete(file)
  }

  useEffect(() => {
    if (!open) return

    const total = files.length
    if (total === 0) {
      if (typeof onClose === 'function') onClose()
      setPendingDeleteFileId(null)
      setPendingNextIndex(null)
      return
    }

    if (pendingDeleteFileId) {
      const stillExists = files.some((f) => f.id === pendingDeleteFileId)
      if (!stillExists) {
        const idx = typeof pendingNextIndex === 'number' ? pendingNextIndex : activeIndex
        setActiveIndex(Math.min(Math.max(idx, 0), total - 1))
        setPendingDeleteFileId(null)
        setPendingNextIndex(null)
        return
      }
    }

    if (activeIndex > total - 1) setActiveIndex(total - 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, open])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && typeof onClose === 'function') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const total = files.length
  const safeActiveIndex = Math.min(Math.max(activeIndex, 0), Math.max(total - 1, 0))
  const current = files[safeActiveIndex]

  return (
    <Portal>
      <div className={styles.galleryOverlay} onClick={onClose}>
        <div className={styles.galleryDialog} onClick={(e) => e.stopPropagation()}>
          <div className={styles.galleryHeader}>
            <div className={styles.galleryTitle}>
              {title} ({Math.min(safeActiveIndex + 1, total)} / {total})
            </div>
            <button type='button' className={styles.galleryClose} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>

          <div className={styles.galleryBody}>
            <button type='button' className={cn(styles.galleryNav, styles.galleryNavPrev)} onClick={goPrev}>
              {'<'}
            </button>

            <div
              className={styles.galleryViewer}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {typeof onRequestDelete === 'function' && (
                <button type='button' className={styles.galleryDelete} onClick={handleDeleteActive}>
                  <TrashIcon />
                </button>
              )}
              {current && (
                <ImageComponent className={styles.galleryImage} src={current.file_webp || current.file} alt='file' />
              )}
            </div>

            <button type='button' className={cn(styles.galleryNav, styles.galleryNavNext)} onClick={goNext}>
              {'>'}
            </button>
          </div>

          <div className={styles.galleryThumbs}>
            {files.map((file, idx) => (
              <button
                key={file.id}
                type='button'
                className={cn(styles.galleryThumb, { [styles.galleryThumbActive]: idx === safeActiveIndex })}
                onClick={() => setActiveIndex(idx)}
              >
                <ImageComponent src={file.file_mini} alt='file' />
              </button>
            ))}
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default PhotoGalleryModal
